import dayjs from "dayjs";
import { Badge, Card, EmptyState, Table, Text } from "@marekl/adminer/components";
import { formatDate } from "@marekl/adminer/format";
import type { Competition } from "../../../lib/queries/competitions";
import { RowLink } from "../../../components/RowLink";
import {
  COMPETITION_STATUS_LABELS,
  COMPETITION_STATUS_VARIANT,
  countdownIfNear,
  formatCompetitionDates,
} from "../../../lib/competitionState";

// The competition table from the manager's side, used twice: „Moje soutěže"
// for a competition_organizer — whose soutěž the dashboard never mentioned at
// all until now — and „Nadcházející soutěže" for an admin. The axis above says
// where the season *is*; this says what to do about it, so both earn a place.

export interface CompetitionsCardProps {
  title: string;
  /** Names the scope when two cards on the page share a title. */
  subtitle?: string;
  competitions: Competition[];
  emptyTitle: string;
  emptyDescription: string;
  isLoading?: boolean;
  error?: unknown;
}

export function CompetitionsCard({
  title,
  subtitle,
  competitions,
  emptyTitle,
  emptyDescription,
  isLoading,
  error,
}: CompetitionsCardProps) {
  const now = dayjs();
  const rows = [...competitions].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <Card>
      <Card.Header title={title} subtitle={subtitle} subtitlePlacement="right" />
      <Card.Content padding="none">
        <Table<Competition>
          rows={rows}
          rowKey={(c) => c.id}
          isLoading={isLoading}
          error={error}
          cards="sm"
          empty={<EmptyState title={emptyTitle} description={emptyDescription} />}
          columns={[
            {
              key: "date",
              header: "Datum",
              render: (c) => (
                <>
                  <div>{formatCompetitionDates(c)}</div>
                  {c.venue && <Text variant="muted">{c.venue}</Text>}
                </>
              ),
            },
            {
              key: "name",
              header: "Soutěž",
              primary: true,
              render: (c) => (
                <>
                  <div>
                    <RowLink to={`/soutez/${c.id}`}>{c.name}</RowLink>
                  </div>
                  <Text variant="muted">
                    {c.entry_deadline
                      ? `Uzávěrka ${formatDate(c.entry_deadline)}${
                          countdownIfNear(c.entry_deadline, now)
                            ? ` · ${countdownIfNear(c.entry_deadline, now)}`
                            : ""
                        }`
                      : "Bez uzávěrky"}
                  </Text>
                </>
              ),
            },
            {
              key: "status",
              header: "Stav",
              render: (c) => (
                <Badge variant={COMPETITION_STATUS_VARIANT[c.status]}>
                  {COMPETITION_STATUS_LABELS[c.status]}
                </Badge>
              ),
            },
            {
              key: "entries",
              header: "Přihlášky",
              numeric: true,
              render: (c) => c.entry_count,
            },
            {
              key: "actions",
              header: "",
              render: (c) => (
                <Table.Actions>
                  <Table.Action appearance="button" to={`/soutez/${c.id}`}>Řídit</Table.Action>
                </Table.Actions>
              ),
            },
          ]}
        />
      </Card.Content>
    </Card>
  );
}
